// Tag options mirror the desktop nav. Rental accounts see the sectioned nav
// (root buttons + Revenue / Operations / Finance sections); every other
// account type sees the flat nav. Values are stable across account types —
// e.g. the residential "Pros" page stores the same 'contacts' value as the
// rental "Contacts" page — so tags saved under one account type still make
// sense under the other.
export const RENTAL_WORKFLOW_TAGS = [
  { viewValue: 'Chat', value: 'chat' },
  { viewValue: 'Overview', value: 'overview' },
  { viewValue: 'Schedule', value: 'schedule' },
  { viewValue: 'Properties', value: 'properties' },
  // Revenue section
  { viewValue: 'Listings', value: 'listings' },
  { viewValue: 'Leasing', value: 'leasing' },
  { viewValue: 'Optimizer', value: 'optimizer' },
  { viewValue: 'Upsells', value: 'upsells' },
  { viewValue: 'Upgrades', value: 'upgrades' },
  { viewValue: 'Reviews', value: 'reviews' },
  { viewValue: 'Inquiries', value: 'inquiries' },
  { viewValue: 'Direct Booking', value: 'direct-booking' },
  { viewValue: 'Wi-Fi Marketing', value: 'wifi-marketing' },
  { viewValue: 'Happiness', value: 'happiness' },
  // Operations section
  { viewValue: 'Messages', value: 'messages' },
  { viewValue: 'Assets', value: 'assets' },
  { viewValue: 'Access', value: 'access' },
  { viewValue: 'Restocking', value: 'restocking' },
  { viewValue: 'Maintenance', value: 'maintenance' },
  { viewValue: 'Checklists', value: 'checklist' },
  { viewValue: 'Compliance', value: 'compliance' },
  { viewValue: 'Noise Monitoring', value: 'noise-monitoring' },
  { viewValue: 'Insurance', value: 'insurance' },
  { viewValue: 'eSignature', value: 'e-sign' },
  { viewValue: 'Guidebook', value: 'guidebook' },
  { viewValue: 'Contacts', value: 'contacts' },
  { viewValue: 'Agents', value: 'agents' },
  // Finance section
  { viewValue: 'Bills', value: 'bills' },
  { viewValue: 'Deposits', value: 'deposits' },
  { viewValue: 'Rent Collection', value: 'rent-collection' },
  { viewValue: 'Leases', value: 'leases' },
  { viewValue: 'Transactions', value: 'transactions' },
  { viewValue: 'Taxes', value: 'taxes' },
  { viewValue: 'Monthly P&L', value: 'monthly-pl' },
];

export const RESIDENTIAL_WORKFLOW_TAGS = [
  { viewValue: 'Chat', value: 'chat' },
  { viewValue: 'Overview', value: 'overview' },
  { viewValue: 'Schedule', value: 'schedule' },
  { viewValue: 'Messages', value: 'messages' },
  { viewValue: 'Maintenance', value: 'maintenance' },
  { viewValue: 'Restocking', value: 'restocking' },
  { viewValue: 'Assets', value: 'assets' },
  { viewValue: 'Checklists', value: 'checklist' },
  { viewValue: 'Pros', value: 'contacts' },
  { viewValue: 'Agents', value: 'agents' },
];

// Tags that used to be selectable but were replaced by more granular options.
// Kept in the union so stored values are still recognized as user-facing.
const LEGACY_WORKFLOW_TAGS = [
  { viewValue: 'Marketing', value: 'marketing' },
  // The Finance nav section only expands/collapses — it never navigates, and
  // each of its tabs (Bills, Taxes, ...) carries its own tag, so there is no
  // page for a 'finance' tag to surface on.
  { viewValue: 'Finance', value: 'finance' },
];

// Union of every known tag across account types. Used to distinguish
// user-facing tags from system tags (e.g. ai_agent) and to resolve display
// labels regardless of which account type stored the tag.
export const WORKFLOW_TAGS = [
  ...RENTAL_WORKFLOW_TAGS,
  ...RESIDENTIAL_WORKFLOW_TAGS.filter((tag) =>
    !RENTAL_WORKFLOW_TAGS.some((rentalTag) => rentalTag.value === tag.value)
  ),
  ...LEGACY_WORKFLOW_TAGS,
];

export function getWorkflowTagsForClient(isRentalClient: boolean): { viewValue: string; value: string }[] {
  return isRentalClient ? RENTAL_WORKFLOW_TAGS : RESIDENTIAL_WORKFLOW_TAGS;
}

// Maps a trigger catalog category to the page tag it implies. The implied tag
// is display/read-time only — it is never persisted on the workflow.
// 'Scheduled' (Every Day/Week/Month) is deliberately unmapped: cron-style
// triggers say nothing about which page the workflow relates to.
// Only categories whose name DIFFERS from the tag value need an entry here.
// Categories that already match a tag value (Maintenance, Messages, Upsells...)
// resolve automatically via the fallback in getImpliedTagForTriggerCategory.
export const TRIGGER_CATEGORY_TO_TAG: Record<string, string> = {
  'Guest Reservations': 'schedule',
  'Jobs': 'schedule',
  'Communication': 'messages',
  'Issues': 'maintenance',
  'Tasks': 'maintenance',
  'Lists': 'checklist',
  'Pros': 'contacts',
  'Invoices': 'bills',
  'Inventory': 'restocking',
};

const USER_FACING_TAG_SET = new Set(WORKFLOW_TAGS.map((tag) => tag.value));

export function isUserFacingTag(tag: string): boolean {
  return USER_FACING_TAG_SET.has(tag);
}

export function getImpliedTagForTriggerCategory(category?: string | null): string | null {
  if (!category) {
    return null;
  }
  const mapped = TRIGGER_CATEGORY_TO_TAG[category];
  if (mapped) {
    return mapped;
  }
  // Self-healing fallback: a trigger category that already matches a tag value
  // (e.g. 'Maintenance', 'Messages', 'Upsells') maps to itself, so newly added
  // trigger categories don't silently come up blank.
  const normalized = category.trim().toLowerCase();
  return USER_FACING_TAG_SET.has(normalized) ? normalized : null;
}

// Effective tags = implied tag (from the workflow's trigger, first) plus the
// explicit user-facing tags stored on the workflow. System tags (ai_agent...)
// are excluded. triggerTypes is the GET /customer/workflow-triggers catalog.
export function getEffectiveWorkflowTags(workflow: any, triggerTypes: any[]): string[] {
  const explicit = (workflow?.tags || []).filter((tag) => isUserFacingTag(tag));
  const version = workflow?.published_version || workflow?.draft_version;
  const versionTrigger = version?.workflow_version_trigger;
  let implied: string | null = null;
  if (versionTrigger) {
    const triggerType = (triggerTypes || []).find((wt) =>
      wt.id === versionTrigger.trigger_id ||
      (wt.name || '').toLowerCase().trim() === (versionTrigger.name || '').toLowerCase().trim()
    );
    implied = getImpliedTagForTriggerCategory(triggerType?.category);
  }
  if (!implied) {
    return explicit;
  }
  return [implied, ...explicit.filter((tag) => tag !== implied)];
}
